import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Input,
  Text,
  VStack,
  Image
} from '@chakra-ui/react';

import { authService } from '../services/authService';
import { usePermissions } from '../hooks/usePermissions';

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loginValue, setLoginValue] = useState('');
  const [passwordValue, setPasswordValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const { isPartner, isAdmin, isUser, isMaster } = usePermissions();

  const handleLogin = async () => {
    // Validação básica
    if (!loginValue || !passwordValue) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Chamar o serviço de autenticação
      const response = await authService.login(loginValue, passwordValue);
      
      console.log('Login realizado com sucesso:', response);
      
      // Verificar o role do usuário e redirecionar adequadamente usando o hook
      if (isMaster()) {
        // Master escolhe em nome de qual concessionária vai emitir
        navigate('/master/select-dealer');
      } else if (isPartner()) {
        // Partner vai para a tela de frota
        navigate('/fleet');
      } else if (isAdmin()) {
        // Admin vai para fleet listing
        navigate('/admin/fleet-listing');
      } else if (isUser()) {
        // User vai para a home
        navigate('/home');
      } else {
        // Fallback para rota raiz (redirecionamento inteligente)
        navigate('/');
      }
    } catch (err: any) {
      // Verificar se é o erro 428 (senha precisa ser alterada)
      if (err.status === 428) {
        // Redirecionar para a página de alteração de senha com o username
        navigate(`/change-password?username=${encodeURIComponent(loginValue)}`, { state: { reason: err.message } });
        return;
      }
      
      // Mostrar mensagem de erro
      console.error('Erro de login:', err);
      setError(err.message || 'Não foi possível fazer login. Verifique seu usuário e senha.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="white" display="flex">
      {/* Lado Esquerdo - Ilustração */}
      <Box
        flex="1"
        bg="linear-gradient(135deg, #3333CC 0%, #2828A0 100%)"
        display={{ base: "none", lg: "flex" }}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
      >
        {/* Logo BS2 */}
        <Box position="absolute" top="40px" left="40px" zIndex={2}>
          <Image 
            src="/banco-bs2-logo-branco-2.png" 
            alt="BS2 Seguros" 
            width="150px" 
            height="auto" 
          />
        </Box>

        {/* Elementos decorativos de fundo */}
        <Box
          position="absolute"
          top="20%"
          right="-10%"
          width="300px"
          height="300px"
          borderRadius="full"
          bg="rgba(255,255,255,0.1)"
          zIndex={1}
        />
        <Box
          position="absolute"
          bottom="10%"
          left="-5%"
          width="200px"
          height="200px"
          borderRadius="full"
          bg="rgba(255,255,255,0.05)"
          zIndex={1}
        />

        {/* Imagem principal */}
        <Box position="relative" zIndex={2} maxW="600px" width="100%" px={8}>
          <Image
            src="/login-illustration.svg"
            alt="Ilustração de segurança e proteção"
            width="100%"
            height="auto"
            objectFit="contain"
          />
        </Box>
      </Box>

      {/* Lado Direito - Formulário de Login */}
      <Box
        flex="1"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="white"
        px={{ base: 6, md: 12 }}
      >
        <Box width="100%" maxW="400px">
          <VStack gap={8} align="stretch">
            {/* Título */}
            <Box textAlign="center">
              <Heading size="xl" color="#1A1A1A" mb={2}>
                Login
              </Heading>
              <Text color="gray.600">
                Acesse sua conta para continuar
              </Text>
            </Box>

            {/* Formulário */}
            <VStack gap={6}>
              {/* Campo de Login */}
              <Box width="100%">
                <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                  Login
                </Text>
                <Input
                  placeholder="Digite seu login"
                  value={loginValue}
                  onChange={(e) => setLoginValue(e.target.value)}
                  size="lg"
                  borderRadius="md"
                  borderColor="#E2E8F0"
                  _hover={{ borderColor: "#3333CC" }}
                  _focus={{ borderColor: "#3333CC", boxShadow: "0 0 0 1px #3333CC" }}
                />
              </Box>

              {/* Campo de Senha */}
              <Box width="100%" position="relative">
                <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                  Senha
                </Text>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Digite sua senha"
                  value={passwordValue}
                  onChange={(e) => setPasswordValue(e.target.value)}
                  size="lg"
                  borderRadius="md"
                  borderColor="#E2E8F0"
                  _hover={{ borderColor: "#3333CC" }}
                  _focus={{ borderColor: "#3333CC", boxShadow: "0 0 0 1px #3333CC" }}
                  pr="50px"
                />
                <Box 
                  position="absolute" 
                  right="12px" 
                  top="calc(50% + 10px)" 
                  transform="translateY(-50%)" 
                  zIndex={2}
                  height="24px"
                  display="flex"
                  alignItems="center"
                >
                  <Box
                    as="button"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    onClick={() => setShowPassword(!showPassword)}
                    color="gray.500"
                    _hover={{ color: "#3333CC" }}
                    height="24px"
                    width="24px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="transparent"
                    border="none"
                    outline="none"
                    cursor="pointer"
                    p={0}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" 
                          fill="currentColor"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C19.17 15.37 15.8 17.5 12 17.5C8.2 17.5 4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5ZM12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 9.5C10.62 9.5 9.5 10.62 9.5 12C9.5 13.38 10.62 14.5 12 14.5C13.38 14.5 14.5 13.38 14.5 12C14.5 10.62 13.38 9.5 12 9.5ZM12 7.5C14.48 7.5 16.5 9.52 16.5 12C16.5 14.48 14.48 16.5 12 16.5C9.52 16.5 7.5 14.48 7.5 12C7.5 9.52 9.52 7.5 12 7.5Z" 
                          fill="currentColor" />
                        <path d="M3.71 2.29L2.29 3.71L20.29 21.71L21.71 20.29L3.71 2.29Z" fill="currentColor" />
                      </svg>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Mensagem de erro */}
              {error && (
                <Box bg="red.50" p={3} borderRadius="md" borderLeft="4px solid" borderLeftColor="red.500">
                  <Text color="red.600" fontSize="sm">{error}</Text>
                </Box>
              )}
              
              {/* Botão de Login */}
              <Button
                bg="#3333CC"
                color="white"
                size="lg"
                width="100%"
                borderRadius="md"
                onClick={handleLogin}
                _hover={{ bg: "#2828A0" }}
                _active={{ bg: "#1F1F80" }}
                boxShadow="0 4px 12px rgba(51,51,204,0.3)"
                fontWeight="bold"
                fontSize="md"
                disabled={isLoading}
              >
                Entrar
              </Button>
            </VStack>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};